/**
 * Show Search Typeahead
 *
 * Attaches autocomplete behaviour to the existing q search input
 * ([data-show-search-input]). When the user selects a show from the
 * dropdown it is added as a removable tag inside the search wrapper
 * ([data-show-search]) and a hidden input[name="show"] is written inside
 * that tag so FormData (and therefore getInlineObj()) collects it as part
 * of the MULTI "show" array.
 *
 * The q input is cleared after a show is selected so the user can either
 * type a new show title or leave it empty. Whatever text remains in q at
 * render time is sent as the free-text filter alongside the show IDs.
 *
 * Removing a tag removes its hidden input and re-renders the results.
 * The global "Clear All" chip removes all tags and hidden inputs silently
 * (the filter bar's own handler calls render() immediately after).
 *
 * Usage:
 *   import mountShowSearch from './showSearch';
 *   mountShowSearch( bar, form, render );
 *
 * Required DOM within bar:
 *   [data-show-search]          - Wrapper element (the search div).
 *   [data-show-search-input]    - The q text/search input.
 *   [data-show-tags]            - Container for selected show tags.
 *   [data-show-search-dropdown] - Container for autocomplete results.
 *
 * @param {HTMLElement} bar    - The filter bar element.
 * @param {HTMLElement} form   - The parent form element.
 * @param {Function}    render - The filter bar render function.
 * @return {{ clear: Function }}
 */

const API_ROOT = (window.wpApiSettings?.root ?? '/wp-json/').replace(/\/$/, '');
const ENDPOINT = `${ API_ROOT }/olt/v1/show-search`;
const MIN_CHARS = 2;
const DEBOUNCE = 300;
const MAX_ITEMS = 10;

export default function mountShowSearch(bar, form, render) {
	const wrapper = bar.querySelector('[data-show-search]');
	const input = bar.querySelector('[data-show-search-input]');
	const tagList = bar.querySelector('[data-show-tags]');
	const dropdown = bar.querySelector('[data-show-search-dropdown]');

	if (!wrapper || !input || !tagList || !dropdown) {
		return { clear: () => {}, getIds: () => [] };
	}

	// Seed the ID set from any server-rendered hidden inputs so that shows
	// already selected on page load are treated as duplicates if typed again.
	// Query bar (not form) so these are found regardless of whether the
	// filter bar is wrapped in a <form> element.
	const selectedIds = new Set(
		Array.from(bar.querySelectorAll('input[type="hidden"][name="shows[]"]'))
			.map((el) => el.value)
			.filter(Boolean)
	);

	// Also seed from any server-rendered tag elements (when the PHP renders
	// visual tags without embedding a hidden input inside each tag).
	// This prevents the UI from "forgetting" selected shows on focus/blur.
	tagList.querySelectorAll('[data-show-tag][data-show-id]').forEach((tag) => {
		const id = tag.getAttribute('data-show-id');
		if (id) {
			selectedIds.add(String(id));
		}
	});

	// Wire remove listeners to any tags that were server-rendered on page load.
	// Dynamically added tags get their listener inside addTag(); these do not.
	// Server-rendered hidden inputs (name="shows[]") live inside the tag in
	// the PHP, so the remove handler removes the whole tag element (which
	// takes its input with it) rather than looking in form.
	tagList.querySelectorAll('[data-show-tag]').forEach((tag) => {
		const id = tag.getAttribute('data-show-id');
		const btn = tag.querySelector('.site-tickets-filters__search-tag-remove');
		if (!btn || !id) {
			return;
		}
		btn.addEventListener('click', () => {
			selectedIds.delete(id);
			tag.remove();
			if (!tagList.querySelector('[data-show-tag]')) {
				tagList.hidden = true;
			}
			if (typeof render === 'function') {
				render();
			}
		});
	});

	let debounceTimer = null;
	let currentResults = [];
	let activeIndex = -1;
	let currentAbort = null;

	// ---- Dropdown ------------------------------------------------

	/** Close and empty the dropdown. */
	const closeDropdown = () => {
		dropdown.hidden = true;
		dropdown.innerHTML = '';
		currentResults = [];
		activeIndex = -1;
		input.setAttribute('aria-expanded', 'false');
		input.removeAttribute('aria-activedescendant');
	};

	/**
	 * Highlight a dropdown option by index.
	 *
	 * @param {number} index
	 */
	const setActiveOption = (index) => {
		const options = dropdown.querySelectorAll('[role="option"]');
		options.forEach((opt, i) => {
			const active = i === index;
			opt.classList.toggle('is-active', active);
			opt.setAttribute('aria-selected', active ? 'true' : 'false');
			if (active) {
				input.setAttribute('aria-activedescendant', opt.id);
			}
		});
		activeIndex = index;
	};

	/**
	 * Render autocomplete results into the dropdown.
	 *
	 * @param {Array<{ id: number, title: string }>} results
	 */
	const renderDropdown = (results) => {
		dropdown.innerHTML = '';

		if (!results.length) {
			dropdown.hidden = true;
			input.setAttribute('aria-expanded', 'false');
			return;
		}

		const list = document.createElement('ul');
		list.setAttribute('role', 'listbox');
		list.id = 'show-search-listbox';
		list.className = 'site-tickets-filters__show-dropdown-list';

		results.forEach((result, index) => {
			const item = document.createElement('li');
			item.setAttribute('role', 'option');
			item.setAttribute('aria-selected', 'false');
			item.id = `show-search-option-${ index }`;
			item.className = 'site-tickets-filters__show-dropdown-item';

			const alreadySelected = selectedIds.has(String(result.id));
			if (alreadySelected) {
				item.classList.add('is-selected');
				item.setAttribute('aria-disabled', 'true');
			}

			item.textContent = result.title;

			// mousedown fires before blur so the dropdown stays open long enough.
			item.addEventListener('mousedown', (e) => {
				e.preventDefault();
				if (!alreadySelected) {
					selectShow(result);
				}
			});

			list.appendChild(item);
		});

		dropdown.appendChild(list);
		dropdown.hidden = false;
		input.setAttribute('aria-expanded', 'true');

		currentResults = results;
		activeIndex = -1;
	};

	// ---- Tags ----------------------------------------------------

	/**
	 * Sync the data-q-suppressed attribute on the input based on whether any
	 * show tags are currently active. filtersCore reads this flag before
	 * deciding whether to inject the typed q value into the request params.
	 */
	const syncQSuppressed = () => {
		if (tagList.querySelector('[data-show-tag]')) {
			input.dataset.qSuppressed = '1';
		} else {
			delete input.dataset.qSuppressed;
		}
	};

	// Set initial suppression state based on any server-rendered tags.
	syncQSuppressed();

	/**
	 * Build and insert a visual tag for a selected show.
	 *
	 * No hidden input is created. The selected ID is tracked exclusively
	 * in the selectedIds Set and exposed via getIds(). Callers read that
	 * in paramsFromForm() rather than relying on FormData, which removes
	 * any dependency on the filter bar being wrapped in a <form> element.
	 *
	 * @param {string|number} id
	 * @param {string}        title
	 */
	const addTag = (id, title) => {
		const tag = document.createElement('span');
		tag.className = 'site-tickets-filters__search-tag';
		tag.setAttribute('data-show-tag', '');
		tag.setAttribute('data-show-id', String(id));

		const label = document.createElement('span');
		label.className = 'site-tickets-filters__search-tag-label';
		label.textContent = title;

		const btn = document.createElement('button');
		btn.type = 'button';
		btn.className = 'site-tickets-filters__search-tag-remove';
		btn.setAttribute('aria-label', `Remove ${ title }`);
		btn.innerHTML = '&times;';

		btn.addEventListener('click', () => {
			selectedIds.delete(String(id));
			tag.remove();

			if (!tagList.querySelector('[data-show-tag]')) {
				tagList.hidden = true;
			}

			syncQSuppressed();

			if (typeof render === 'function') {
				render();
			}
		});

		tag.append(label, btn);
		tagList.appendChild(tag);
		tagList.hidden = false;
		syncQSuppressed();
	};

	/**
	 * Remove all show tags and hidden inputs, reset the ID set.
	 * Called by the global "Clear All" chip listener.
	 */
	const clearAllShows = () => {
		tagList.querySelectorAll('[data-show-tag]').forEach((t) => t.remove());
		form.querySelectorAll('input[data-show-form-input]').forEach((i) => i.remove());
		bar.querySelectorAll('input[type="hidden"][name="shows[]"]').forEach((i) => i.remove());
		tagList.hidden = true;
		selectedIds.clear();
		syncQSuppressed();
	};

	// ---- Selection -----------------------------------------------

	/**
	 * Commit a selected result: add the tag, clear the input, close
	 * the dropdown, then call render.
	 *
	 * @param {{ id: number, title: string }} result
	 */
	const selectShow = (result) => {
		const id = String(result.id);

		if (!selectedIds.has(id)) {
			selectedIds.add(id);
			addTag(id, result.title);
		}

		// Clear the text field so the user can search for another show.
		// Whatever they type next will become the q filter if they do not
		// select another show before render.
		input.value = '';
		closeDropdown();

		if (typeof render === 'function') {
			render();
		}
	};

	// ---- Fetch ---------------------------------------------------

	/**
	 * Fetch matching shows from the REST endpoint.
	 *
	 * @param {string} query
	 * @return {Promise<Array<{ id: number, title: string }>>}
	 */
	const fetchShows = async (query) => {
		if (currentAbort) {
			currentAbort.abort();
		}
		currentAbort = new AbortController();

		try {
			const url = new URL(ENDPOINT, window.location.origin);
			url.searchParams.set('q', query);
			url.searchParams.set('limit', String(MAX_ITEMS));

			const response = await fetch(url.toString(), {
				credentials: 'same-origin',
				headers: { Accept: 'application/json' },
				signal: currentAbort.signal
			});

			if (!response.ok) {
				return [];
			}

			const data = await response.json();
			return Array.isArray(data.shows) ? data.shows : [];
		} catch (err) {
			if (err.name !== 'AbortError') {
				// eslint-disable-next-line no-console
				console.warn('[showSearch] Fetch error:', err);
			}
			return [];
		} finally {
			currentAbort = null;
		}
	};

	// ---- Event listeners -----------------------------------------

	input.addEventListener('input', () => {
		clearTimeout(debounceTimer);

		const query = input.value.trim();

		if (query.length < MIN_CHARS) {
			closeDropdown();
			return;
		}

		debounceTimer = setTimeout(async () => {
			const results = await fetchShows(query);
			renderDropdown(results);
		}, DEBOUNCE);
	});

	input.addEventListener('keydown', (e) => {
		if (dropdown.hidden) {
			return;
		}

		// Navigate only over options that are not already selected.
		const options = Array.from(dropdown.querySelectorAll('[role="option"]'));
		const count = options.length;

		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				setActiveOption(activeIndex < count - 1 ? activeIndex + 1 : 0);
				break;

			case 'ArrowUp':
				e.preventDefault();
				setActiveOption(activeIndex > 0 ? activeIndex - 1 : count - 1);
				break;

			case 'Enter':
				if (activeIndex >= 0 && currentResults[ activeIndex ]) {
					const el = dropdown.querySelector(`#show-search-option-${ activeIndex }`);
					const disabled = el?.getAttribute('aria-disabled') === 'true';
					if (!disabled) {
						e.preventDefault();
						selectShow(currentResults[ activeIndex ]);
					}
				}
				break;

			case 'Escape':
				closeDropdown();
				break;
		}
	});

	// Delay close so a mousedown on a result fires before blur.
	input.addEventListener('blur', () => {
		setTimeout(closeDropdown, 200);
	});

	// Close when focus moves outside the search wrapper.
	document.addEventListener('click', (e) => {
		if (!wrapper.contains(e.target)) {
			closeDropdown();
		}
	});

	// Clear all show tags ONLY when the actual Clear link/button is clicked.
	// Do NOT clear shows on incidental clicks within the clear-chip wrapper,
	// as that can be large/overlapping in some layouts.
	bar.addEventListener('click', (e) => {
		const clearLink = e.target.closest('[data-clear-chip-link]');
		if (!clearLink) {
			return;
		}
		clearAllShows();
	});

	// ---- Public API ----------------------------------------------
	return {
		/**
		 * Remove all selected shows. Call from any external reset that
		 * does not go through the standard filter bar clear chip.
		 */
		clear: clearAllShows,

		/**
		 * Return the current selected show IDs as an array of positive
		 * integers. Callers use this in paramsFromForm() rather than
		 * reading hidden inputs via FormData, which removes any dependency
		 * on the filter bar being nested inside a <form> element.
		 *
		 * @return {number[]}
		 */
		getIds: () => [...selectedIds].map(Number).filter((v) => v > 0)
	};
}